/**
 * Baseline Registry — canonical definitions for every supported baseline metric.
 *
 * Replaces the legacy context-dictionary.ts BASELINE_ENTRIES with richer
 * metadata: classification, valid ranges, review priority, scope dimensions,
 * dependency tracking, and definition notes.
 */

// ── Types ────────────────────────────────────────────────────────────────────

/** Namespace reserved for opportunity-local ROI facts, never org baselines. */
export const OPPORTUNITY_LOCAL_BASELINE_PREFIX = "local_";

/**
 * A baseline captured for one opportunity because the organization profile has
 * no equivalent. It remains part of the persisted model context rather than an
 * organization-wide profile value.
 */
export interface OpportunityLocalBaseline {
  /** Formula token, referenced as `c.<key>`. */
  key: string;
  label: string;
  unit: string;
  value: number;
  /** The metric this opportunity-local value supports. */
  metricId: string;
  capturedAt: string;
  capturedBy: string | null;
}

export interface BaselineDefinition {
  key: string;
  label: string;
  description: string;
  aliases?: readonly string[];
  /**
   * Source units for aliases whose numeric values are expressed differently
   * from this definition's canonical unit. Keys must be explicit aliases;
   * undeclared aliases and canonical keys are always treated as canonical-unit
   * values.
   */
  aliasInputUnits?: Readonly<Record<string, "minutes" | "hours" | "days">>;
  class: "observed" | "derived" | "benchmark" | "factor" | "org_context";
  unit:
    | "count"
    | "currency"
    | "percent"
    | "hours"
    | "minutes"
    | "days"
    | "fte"
    | "ratio"
    | "index";
  /**
   * Physical-impossibility bounds. Values outside these are hard-rejected by
   * the intake normalizer and the profile-service write path. Use sparingly:
   * negative counts/currency/durations, percent > 1 (after the 0–100 → 0–1
   * normalization), etc. `max` is optional — omit when there is no
   * physically-impossible upper bound (e.g. a count or a dollar figure).
   */
  hardBounds: { min: number; max?: number };
  /**
   * Soft expected band. Values outside these are still accepted, but the
   * normalizer stamps a warning into `normalizationNotes` so the UI / model
   * can flag the value as unusually high or low. Use this for sanity caps
   * like "avg appointment value over $1,000 is suspicious" — historically
   * these lived in `validRange` and caused legitimate outlier values to be
   * rejected.
   */
  sanityRange?: { min?: number; max?: number };
  reviewPriority: "critical" | "important" | "supporting" | "internal";
  scopeDimensions: Array<"payer" | "setting" | "specialty" | "site">;
  dependsOn: string[];
  definitionNotes: {
    numerator?: string;
    denominator?: string;
    inclusions?: string[];
    exclusions?: string[];
  };
}

// ── Registry ─────────────────────────────────────────────────────────────────

export const BASELINE_REGISTRY: readonly BaselineDefinition[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // OBSERVED — directly measurable from organisation data
  // ═══════════════════════════════════════════════════════════════════════════

  {
    key: "overall_denial_rate",
    label: "Overall Denial Rate",
    description: "Percentage of claims denied across all categories",
    aliases: ["denial rate", "claim denial rate", "overall claim denial rate"],
    class: "observed",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "critical",
    scopeDimensions: ["payer", "setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Total denied claims",
      denominator: "Total submitted claims",
    },
  },
  {
    key: "no_show_rate",
    label: "No-Show Rate",
    description:
      "Percentage of scheduled appointments where patients do not appear",
    aliases: ["no show rate", "patient no show rate", "noshow rate"],
    class: "observed",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "critical",
    scopeDimensions: ["specialty", "setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Missed appointments",
      denominator: "Scheduled appointments",
    },
  },
  {
    key: "pa_volume_annual",
    label: "Annual PA Volume",
    description: "Total prior authorization requests processed per year",
    aliases: [
      "pa volume",
      "prior auth volume",
      "prior authorization volume",
      "annual prior auth volume",
    ],
    class: "observed",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "critical",
    scopeDimensions: ["payer", "specialty"],
    dependsOn: [],
    definitionNotes: {
      inclusions: [
        "All prior auth requests submitted to payers within the fiscal year",
      ],
    },
  },
  {
    key: "call_volume_annual",
    label: "Annual Call Volume",
    description: "Total inbound patient calls per year",
    aliases: [
      "call volume",
      "patient call volume",
      "annual patient call volume",
    ],
    class: "observed",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "important",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "annual_appointments",
    label: "Annual Appointments",
    description: "Total number of patient appointments per year",
    aliases: [
      "appointment volume",
      "annual visit volume",
      "yearly appointments",
    ],
    class: "observed",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "critical",
    scopeDimensions: ["specialty", "setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_claim_value",
    label: "Average Claim Value",
    description: "Average dollar value per insurance claim processed",
    aliases: [
      "claim value",
      "average reimbursement per claim",
      "average revenue per claim",
    ],
    class: "observed",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 100_000 },
    reviewPriority: "critical",
    scopeDimensions: ["payer", "setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Total net revenue",
      denominator: "Total encounters",
    },
  },
  {
    key: "current_cmi",
    label: "Current CMI",
    description: "Case mix index reflecting average patient acuity",
    aliases: ["cmi", "case mix index"],
    class: "observed",
    unit: "index",
    hardBounds: { min: 0 },
    sanityRange: { min: 0.5, max: 5 },
    reviewPriority: "critical",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "or_utilization_rate",
    label: "OR Utilization Rate",
    description: "Percentage of available operating room hours in use",
    class: "observed",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["site"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Utilized OR hours",
      denominator: "Available OR hours",
    },
  },
  {
    key: "inpatient_discharges",
    label: "Inpatient Discharges",
    description: "Annual count of inpatient discharges",
    class: "observed",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "important",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "annual_surgical_cases",
    label: "Annual Surgical Cases",
    description: "Total surgical procedures performed per year",
    class: "observed",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "important",
    scopeDimensions: ["specialty", "site"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "self_pay_pct",
    label: "Self-Pay Percentage",
    description: "Share of encounters paid out-of-pocket by patients",
    class: "observed",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Self-pay encounters",
      denominator: "Total encounters",
    },
  },
  {
    key: "avg_revenue_per_or_hour",
    label: "Avg Revenue per OR Hour",
    description: "Average revenue generated per hour of operating room use",
    class: "observed",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 50_000 },
    reviewPriority: "important",
    scopeDimensions: ["site"],
    dependsOn: ["annual_surgical_cases", "or_count", "or_utilization_rate"],
    definitionNotes: {
      numerator: "Surgical revenue",
      denominator: "OR hours per year",
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DERIVED — computed from other baselines
  // ═══════════════════════════════════════════════════════════════════════════

  {
    key: "coding_denial_rate",
    label: "Coding Denial Rate",
    description: "Percentage of claims denied due to coding errors",
    aliases: ["coding denial percent", "coding-related denial rate"],
    class: "derived",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["payer", "setting"],
    dependsOn: ["overall_denial_rate"],
    definitionNotes: {
      numerator: "Coding-related denials",
      denominator: "Total submitted claims",
    },
  },
  {
    key: "weighted_avg_coding_time",
    label: "Weighted Avg Coding Time",
    description:
      "Setting-weighted average minutes to code an encounter across all care settings",
    class: "derived",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 60 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [
      "avg_coding_time_minutes_ip",
      "avg_coding_time_minutes_op",
      "avg_coding_time_minutes_ed",
      "avg_coding_time_minutes_rad",
    ],
    definitionNotes: {},
  },
  {
    key: "avg_denial_value",
    label: "Average Denial Value",
    description:
      "Average dollar value of a denied claim (typically ~80% of avg claim value)",
    class: "derived",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 100_000 },
    reviewPriority: "important",
    scopeDimensions: ["payer"],
    dependsOn: ["avg_claim_value"],
    definitionNotes: {},
  },
  {
    key: "avg_appointment_value",
    label: "Average Appointment Value",
    description: "Average revenue per appointment, capped at $350",
    class: "derived",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 2_000 },
    reviewPriority: "important",
    scopeDimensions: ["specialty"],
    dependsOn: ["avg_claim_value"],
    definitionNotes: {},
  },
  {
    key: "or_count",
    label: "Operating Room Count",
    description:
      "Estimated number of operating rooms (~1 per 1,000 surgical cases/year)",
    class: "derived",
    unit: "count",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 500 },
    reviewPriority: "supporting",
    scopeDimensions: ["site"],
    dependsOn: ["annual_surgical_cases"],
    definitionNotes: {},
  },
  {
    key: "avg_coding_time_minutes_ip",
    label: "Avg Coding Time — Inpatient",
    description: "Average minutes to code an inpatient encounter",
    class: "derived",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 120 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_coding_time_minutes_op",
    label: "Avg Coding Time — Outpatient",
    description: "Average minutes to code an outpatient encounter",
    class: "derived",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 60 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_coding_time_minutes_ed",
    label: "Avg Coding Time — ED",
    description: "Average minutes to code an emergency department encounter",
    class: "derived",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 60 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_coding_time_minutes_rad",
    label: "Avg Coding Time — Radiology",
    description: "Average minutes to code a radiology encounter",
    class: "derived",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 30 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BENCHMARK — fixed values from industry benchmarks (BASELINE_BENCHMARKS)
  // ═══════════════════════════════════════════════════════════════════════════

  {
    key: "avg_pa_handling_time_minutes",
    label: "Avg PA Handling Time",
    description: "Average minutes spent per prior authorization request",
    aliases: [
      "pa handling time",
      "prior auth handling time",
      "average prior auth handling time",
      "staff hours per PA",
      "staff time per PA",
    ],
    aliasInputUnits: { "staff hours per PA": "hours" },
    class: "benchmark",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 120 },
    reviewPriority: "important",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "physician_burnout_rate",
    label: "Physician Burnout Rate",
    description: "Percentage of physicians reporting burnout symptoms",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_call_handle_time_minutes",
    label: "Avg Call Handle Time",
    description: "Average minutes per inbound patient call",
    aliases: [
      "call handle time",
      "average handle time",
      "aht",
      "average call handling time",
    ],
    class: "benchmark",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 0.5, max: 60 },
    reviewPriority: "important",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "call_abandonment_rate",
    label: "Call Abandonment Rate",
    description: "Percentage of inbound calls abandoned before answer",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Abandoned calls",
      denominator: "Total inbound calls",
    },
  },
  {
    key: "denial_recovery_rate",
    label: "Denial Recovery Rate",
    description: "Percentage of denied claims successfully recovered on appeal",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Recovered denial dollars",
      denominator: "Total denial dollars",
    },
  },
  {
    key: "denial_rework_cost",
    label: "Denial Rework Cost",
    description: "Average cost to rework a single denied claim",
    class: "benchmark",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 500 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "cancellation_rate",
    label: "Cancellation Rate",
    description: "Percentage of ambulatory appointments cancelled",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Cancelled appointments",
      denominator: "Scheduled appointments",
    },
  },
  {
    key: "current_backfill_rate",
    label: "Current Backfill Rate",
    description:
      "Percentage of cancelled appointment slots currently backfilled",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Backfilled cancelled slots",
      denominator: "Total cancelled slots",
    },
  },
  {
    key: "avg_doc_time_per_encounter_minutes",
    label: "Avg Documentation Time per Encounter",
    description: "Average clinician documentation minutes per encounter",
    class: "benchmark",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 120 },
    reviewPriority: "important",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "coding_accuracy_rate",
    label: "Coding Accuracy Rate",
    description: "Percentage of encounters coded correctly on first pass",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Correctly coded encounters",
      denominator: "Total coded encounters",
    },
  },
  {
    key: "pa_approval_rate",
    label: "PA Approval Rate",
    description: "Percentage of prior authorization requests approved",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "important",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Approved PA requests",
      denominator: "Total PA requests submitted",
    },
  },
  {
    key: "pa_turnaround_hours",
    label: "PA Turnaround Hours",
    description: "Average hours to receive a prior authorization decision",
    class: "benchmark",
    unit: "hours",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 720 },
    reviewPriority: "supporting",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "avg_wrvus_per_physician_per_month",
    label: "Avg wRVUs per Physician per Month",
    description:
      "Average work relative value units generated per physician per month",
    class: "benchmark",
    unit: "count",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 2000 },
    reviewPriority: "supporting",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "cdi_review_rate",
    label: "CDI Review Rate",
    description:
      "Percentage of inpatient discharges reviewed by CDI specialists",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Discharges reviewed by CDI",
      denominator: "Total inpatient discharges",
    },
  },
  {
    key: "documentation_driven_denial_rate",
    label: "Documentation-Driven Denial Rate",
    description:
      "Percentage of denials attributable to documentation specificity issues",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Documentation-specificity denials",
      denominator: "Total denials",
    },
  },
  {
    key: "registration_denial_rate",
    label: "Registration Denial Rate",
    description:
      "Percentage of denials driven by registration or eligibility errors",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {
      numerator: "Registration-driven denials",
      denominator: "Total denials",
    },
  },
  {
    key: "avg_verification_time_minutes",
    label: "Avg Verification Time",
    description: "Average minutes to verify patient insurance eligibility",
    class: "benchmark",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 120 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "missed_charge_rate_surgical",
    label: "Missed Charge Rate — Surgical",
    description: "Percentage of surgical charges not captured",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    sanityRange: { min: 0, max: 0.5 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Missed surgical charges",
      denominator: "Total expected surgical charges",
    },
  },
  {
    key: "missed_charge_rate_ed",
    label: "Missed Charge Rate — ED",
    description: "Percentage of emergency department charges not captured",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    sanityRange: { min: 0, max: 0.5 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Missed ED charges",
      denominator: "Total expected ED charges",
    },
  },
  {
    key: "missed_charge_rate_outpatient",
    label: "Missed Charge Rate — Outpatient",
    description: "Percentage of outpatient charges not captured",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    sanityRange: { min: 0, max: 0.5 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Missed outpatient charges",
      denominator: "Total expected outpatient charges",
    },
  },
  {
    key: "charge_lag_days",
    label: "Charge Lag Days",
    description: "Average days between service and charge posting",
    class: "benchmark",
    unit: "days",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 30 },
    reviewPriority: "supporting",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "block_utilization_rate",
    label: "Block Utilization Rate",
    description: "Percentage of allocated OR block time actually used",
    class: "benchmark",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "supporting",
    scopeDimensions: ["site"],
    dependsOn: [],
    definitionNotes: {
      numerator: "Used block minutes",
      denominator: "Allocated block minutes",
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FACTOR — model-internal indices and multipliers
  // ═══════════════════════════════════════════════════════════════════════════

  {
    key: "denial_revenue_index",
    label: "Denial Revenue Index",
    description:
      "Payer-weighted index reflecting relative revenue impact of denials",
    class: "factor",
    unit: "index",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "noshow_revenue_index",
    label: "No-Show Revenue Index",
    description:
      "Payer-weighted index reflecting relative revenue impact of no-shows",
    class: "factor",
    unit: "index",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "net_discovery_index",
    label: "Net Discovery Index",
    description:
      "Index for potential insurance coverage discovery among self-pay patients",
    class: "factor",
    unit: "index",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "marginal_prevention_factor",
    label: "Marginal Prevention Factor",
    description:
      "Diminishing-returns factor for incremental denial prevention gains",
    class: "factor",
    unit: "ratio",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 2 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: ["org_denial_rate"],
    definitionNotes: {},
  },
  {
    key: "cmi_durability",
    label: "CMI Durability",
    description:
      "Factor reflecting how sustainable CDI-driven CMI improvements are",
    class: "factor",
    unit: "ratio",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 2 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: ["current_cmi"],
    definitionNotes: {},
  },
  {
    key: "surgical_revenue_index",
    label: "Surgical Revenue Index",
    description:
      "Payer-weighted index reflecting relative revenue intensity of surgical cases",
    class: "factor",
    unit: "index",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "weighted_contribution_margin",
    label: "Weighted Contribution Margin",
    description:
      "Weighted average contribution margin used in PA automation ROI calculations",
    class: "factor",
    unit: "ratio",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "ambient_weighted_physician_cost",
    label: "Ambient Weighted Physician Cost",
    description:
      "Specialty-weighted average physician cost for ambient documentation ROI",
    class: "factor",
    unit: "currency",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 1_000_000 },
    reviewPriority: "internal",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "weighted_ip_coding_time",
    label: "Weighted IP Coding Time",
    description:
      "Specialty-weighted average inpatient coding time for autonomous coding ROI",
    class: "factor",
    unit: "minutes",
    hardBounds: { min: 0 },
    sanityRange: { min: 1, max: 120 },
    reviewPriority: "internal",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "self_pay_setting_adjustment",
    label: "Self-Pay Setting Adjustment",
    description:
      "Setting-weighted adjustment factor for self-pay financial clearance models",
    class: "factor",
    unit: "ratio",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: ["setting"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "qa_review_rate",
    label: "QA Review Rate",
    description: "Default quality-assurance review rate for coding output",
    class: "factor",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "call_resolution_rate",
    label: "Call Resolution Rate",
    description: "Default first-call resolution rate for patient call models",
    aliases: ["first call resolution", "fcr", "call resolution percent"],
    class: "factor",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "internal",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "specialty_noshow_value_multiplier",
    label: "Specialty No-Show Value Multiplier",
    description:
      "Specialty-weighted multiplier reflecting relative revenue loss per no-show",
    class: "factor",
    unit: "ratio",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5 },
    reviewPriority: "internal",
    scopeDimensions: ["specialty"],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "org_denial_rate",
    label: "Org Denial Rate",
    description:
      "Payer-weighted organisational denial rate (V3 recalculated, may differ from overall_denial_rate)",
    class: "factor",
    unit: "percent",
    hardBounds: { min: 0, max: 1 },
    reviewPriority: "internal",
    scopeDimensions: ["payer"],
    dependsOn: [],
    definitionNotes: {
      numerator: "V3 payer-weighted denied claims",
      denominator: "V3 payer-weighted total claims",
    },
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // ORG_CONTEXT — populated from the organizational profile, not baseline records
  // ═══════════════════════════════════════════════════════════════════════════

  {
    key: "bedCount",
    label: "Licensed Beds",
    description: "Total licensed beds",
    aliases: ["bed_count", "licensed bed count"],
    class: "org_context",
    unit: "count",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 10_000 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "annualRevenue",
    label: "Annual Revenue",
    description: "Total annual operating revenue",
    class: "org_context",
    unit: "currency",
    hardBounds: { min: 0 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "totalEncounters",
    label: "Total Encounters",
    description: "Total annual patient encounters",
    aliases: ["total_encounters"],
    class: "org_context",
    unit: "count",
    hardBounds: { min: 0 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "workforce_physician",
    label: "Physicians (FTE)",
    description: "Full-time equivalent physician count",
    aliases: ["workforce_physicians"],
    class: "org_context",
    unit: "fte",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 10_000 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "workforce_nurse",
    label: "Nurses (FTE)",
    description: "Full-time equivalent nursing staff count",
    aliases: ["workforce_nurses"],
    class: "org_context",
    unit: "fte",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 50_000 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
  {
    key: "workforce_coder",
    label: "Coders (FTE)",
    description: "Full-time equivalent medical coding staff count",
    aliases: ["workforce_coders"],
    class: "org_context",
    unit: "fte",
    hardBounds: { min: 0 },
    sanityRange: { min: 0, max: 5_000 },
    reviewPriority: "supporting",
    scopeDimensions: [],
    dependsOn: [],
    definitionNotes: {},
  },
] as const;

// ── Lookup index (built once) ────────────────────────────────────────────────

const _index = new Map<string, BaselineDefinition>();
const _aliasIndex = new Map<string, string>();
const _aliasInputUnitIndex = new Map<
  string,
  { key: string; unit: "minutes" | "hours" | "days" }
>();
for (const entry of BASELINE_REGISTRY) {
  _index.set(entry.key, entry);
  const normalizedKey = entry.key.trim().toLowerCase();
  _aliasIndex.set(normalizedKey, entry.key);
  _aliasIndex.set(entry.label.trim().toLowerCase(), entry.key);
  for (const alias of entry.aliases ?? []) {
    _aliasIndex.set(alias.trim().toLowerCase(), entry.key);
  }
  for (const [alias, unit] of Object.entries(entry.aliasInputUnits ?? {})) {
    _aliasInputUnitIndex.set(alias.trim().toLowerCase(), {
      key: entry.key,
      unit,
    });
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Look up a single baseline definition by key or known alias. */
export function getBaselineDefinition(
  key: string,
): BaselineDefinition | undefined {
  const direct = _index.get(key);
  if (direct) return direct;
  // Fall back to alias lookup (e.g. "bed_count" → "bedCount" org_context entry)
  const canonical = _aliasIndex.get(key.trim().toLowerCase());
  return canonical ? _index.get(canonical) : undefined;
}

/** All baselines eligible for use in ROI formulas (excludes model-internal factors and org context). */
export function getFormulaEligibleBaselineDefinitions(): BaselineDefinition[] {
  return BASELINE_REGISTRY.filter(
    (e) => e.class !== "factor" && e.class !== "org_context",
  );
}

/** All baselines eligible for prompt / display contexts (excludes model-internal factors and org context). */
export function getPromptEligibleBaselineDefinitions(): BaselineDefinition[] {
  return BASELINE_REGISTRY.filter(
    (e) => e.class !== "factor" && e.class !== "org_context",
  );
}

/** Returns the human-readable label for a baseline key, or the key itself if not found. */
export function getBaselineDisplayLabel(key: string): string {
  return _index.get(key)?.label ?? key;
}

/** Check if a key is known in the unified baseline registry (any class). */
export function isKnownFormulaKey(key: string): boolean {
  return _index.has(key);
}

/** Resolve a baseline key, label, or configured alias to a canonical key. */
export function resolveBaselineAlias(input: string): string | undefined {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return undefined;
  return _aliasIndex.get(normalized);
}

export const TIME_UNIT_MINUTES = {
  minutes: 1,
  hours: 60,
  days: 1_440,
} as const;

/**
 * Resolve a baseline name and convert a numeric value only when that exact
 * configured alias declares a source time unit. This deliberately performs no
 * label inference or suffix guessing.
 */
export function resolveBaselineValue(
  input: string,
  value: number,
): { key: string; value: number } | undefined {
  const normalized = input.trim().toLowerCase();
  const key = resolveBaselineAlias(input);
  if (!key) return undefined;

  const definition = _index.get(key);
  const aliasInput = _aliasInputUnitIndex.get(normalized);
  if (
    !definition ||
    !aliasInput ||
    aliasInput.key !== key ||
    !(definition.unit in TIME_UNIT_MINUTES)
  ) {
    return { key, value };
  }

  const canonicalUnit = definition.unit as keyof typeof TIME_UNIT_MINUTES;
  return {
    key,
    value:
      (value * TIME_UNIT_MINUTES[aliasInput.unit]) /
      TIME_UNIT_MINUTES[canonicalUnit],
  };
}

/** Return all org_context definitions (populated from profile, not baseline records). */
export function getOrgContextDefinitions(): BaselineDefinition[] {
  return BASELINE_REGISTRY.filter((e) => e.class === "org_context");
}
